import React, { useCallback, useMemo } from 'react';
import { Box, Typography, Button, List, ListItem } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { useTheme } from '@mui/material/styles';

import AzimuthSwatch from 'components/AzimuthSwatch';
import ElevationSwatch from 'components/ElevationSwatch';

import {
    toTimeDDMonYearFormat,
    toReadableTimeFromNow,
    downloadCSVFromJson,
    round,
} from 'utils';
import { ScheduleManager } from 'models';

interface Props {
    scheduleId: number;
    steps: ScheduleManager.Step[];
}

const StepPlan: React.FunctionComponent<Props> = ({ scheduleId, steps }) => {
    const theme = useTheme();

    const fixedSteps = useMemo(
        () =>
            steps.map((step) => ({
                ...step,
                isodatetime: new Date(step.time * 1000),
            })),
        [steps],
    );

    const duration = useMemo(() => {
        if (steps.length < 2) return 0;
        return round(steps[steps.length - 1].time - steps[0].time);
    }, [steps]);

    const downloadCSV = useCallback(() => {
        downloadCSVFromJson(`schedule-${scheduleId}-stepplan.csv`, fixedSteps);
    }, [steps]);

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                }}
            >
                {fixedSteps.length > 0 ? (
                    <>
                        <Typography>
                            Starting:{' '}
                            {toTimeDDMonYearFormat(fixedSteps[0].isodatetime)} (
                            {toReadableTimeFromNow(fixedSteps[0].isodatetime)})
                        </Typography>
                        <Typography>Duration: {duration}s</Typography>
                    </>
                ) : (
                    <Typography>
                        No Steps are able to be parsed for this Schedule
                    </Typography>
                )}
                <Button variant="contained" onClick={downloadCSV}>
                    <DownloadIcon />
                    Download CSV
                </Button>
            </Box>
            <List>
                {fixedSteps.map((step, idx) => (
                    <ListItem
                        key={idx}
                        sx={{
                            backgroundColor:
                                idx % 2 == 0
                                    ? theme.palette.common.neutralXLight
                                    : theme.palette.common.neutralXXLight,
                        }}
                    >
                        <Typography variant="subtitle1">
                            <b>
                                #{idx} - {step.isodatetime.toLocaleTimeString()}
                            </b>
                        </Typography>
                        <Typography>
                            <AzimuthSwatch
                                rotation={step.az}
                                size={32}
                                margin="0px 16px"
                            />
                            Az={round(step.az, 2)}°
                        </Typography>
                        <Typography>
                            <ElevationSwatch
                                elevation={step.el}
                                size={32}
                                margin="0px 16px"
                            />
                            El={round(step.el, 2)}°
                        </Typography>
                    </ListItem>
                ))}
            </List>
        </Box>
    );
};

StepPlan.displayName = 'StepPlan';
export default StepPlan;
